<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import type { Component } from 'vue';

import Inquiry from '@/javascripts/utils/inquiry';

import { useMobileFlag } from '@/javascripts/composables/browser';
import { useInquiryValidation } from '@/javascripts/composables/inquiry/validation';

// コンポーネント
import SlideIndicator from './top_form/top_slide_indicator.vue';
import EmploymentTypeSlide from './top_form/top_slide1.vue';
import WishfulTimesSlide from './top_form/top_slide2.vue';
import PrefCityPositionTypeSlide from './top_form/top_slide3.vue';
import NameBirthYearSlide from './top_form/top_slide4.vue';
import PhoneEmailCommentSlide from './top_form/top_slide5.vue';
import LoadingAnimation from '@/javascripts/common/loading_animation.vue';

/** 問い合わせオブジェクト */
const inquiry = reactive(new Inquiry);

/** スライドコンポーネントリスト */
const slideComponents: Component[] = [
  EmploymentTypeSlide,
  WishfulTimesSlide,
  PrefCityPositionTypeSlide,
  NameBirthYearSlide,
  PhoneEmailCommentSlide
];

// 雇用形態別LPかつ雇用形態が正社員用LPの場合、希望雇用形態を正社員とし、希望雇用形態選択スライドは表示させない。
if (Inquiry.specializedJobTypeLpFlag && Inquiry.employmentTypeOnlyFull) { slideComponents.shift();}

/** 現在のスライド */
const currentSlide = ref(1);
/** 最後のスライド */
const maxSlide = slideComponents.length;
/** 現在のスライドコンポーネント */
const currentSlideComponent = computed(() => slideComponents[currentSlide.value - 1]);
/** トランジション名 */
const transitionName = ref<'next' | 'back'>('next');
/** 問い合わせのバリデーション */
const v$ = useInquiryValidation(inquiry);
/** 遷移中フラグ */
const isTransitioning = ref(false);
/** ローディングアニメーションコンポーネント */
const loadingAnimation = ref<InstanceType<typeof LoadingAnimation> | null>();

/** inquiryの各プロパティ更新ハンドラー */
const updateEmploymentTypeCd = (value: string) => { inquiry.employmentTypeCd = value; };
const updateWishfulTimeCd = (value: string) => { inquiry.wishfulTimeCd = value; };
const updatePrefectureCd = (value: number) => { inquiry.prefectureCd = value; };
const updateCityCd = (value: number) => { inquiry.cityCd = value; };
const updatePositionTypeCds = (value: string[]) => { inquiry.positionTypeCds = value; };
const updateLastName = (value: string) => { inquiry.lastName = value; };
const updateFirstName = (value: string) => { inquiry.firstName = value; };
const updateLastNameKana = (value: string) => { inquiry.lastNameKana = value; };
const updateFirstNameKana = (value: string) => { inquiry.firstNameKana = value; };
const updateBirthYearCd = (value: string) => { inquiry.birthYearCd = value; };
const updatePhone = (value: string) => { inquiry.phone = value; };
const updateEmail = (value: string) => { inquiry.email = value; };
const updateComment = (value: string) => { inquiry.comment = value; };

/** `next`イベントハンドラー */
const next = () => {
  if (isTransitioning.value) return;

  transitionName.value = 'next';
  currentSlide.value++;
}

/** `back`イベントハンドラー */
const back = () => {
  if (isTransitioning.value) return;

  transitionName.value = 'back';
  currentSlide.value--;
}

/** 問い合わせデータを送信 */
const post = () => {
  loadingAnimation.value?.show();
  return inquiry.post();
}

/** モバイルかどうか */
const isMobile = useMobileFlag();
/** SPの場合はフォームのトップにスクロールさせる */
const scrollToFormTopWhenMobile = () => {
  if(isMobile.value) document.getElementById('form').scrollIntoView();
}
</script>

<template>
  <div id="form" class="mv_form">
    <div class="form_header">
      <h2 class="header_title">無料転職サービスへのお問い合わせ</h2>
    </div>
    <div class="form_body">
      <SlideIndicator
        :max="maxSlide"
        :current="currentSlide"
      />
      <LoadingAnimation ref="loadingAnimation" />
      <Transition
        :name="transitionName"
        mode="out-in"
        @leave="scrollToFormTopWhenMobile"
        @before-leave="isTransitioning = true"
        @before-enter="isTransitioning = true"
        @after-enter="isTransitioning = false"
      >
        <KeepAlive>
          <component
            :is="currentSlideComponent"
            :v$="v$"
            :employment-type-cd="inquiry.employmentTypeCd"
            :wishful-time-cd="inquiry.wishfulTimeCd"
            :prefecture-cd="inquiry.prefectureCd"
            :city-cd="inquiry.cityCd"
            :position-type-cds="inquiry.positionTypeCds"
            :last-name="inquiry.lastName"
            :first-name="inquiry.firstName"
            :last-name-kana="inquiry.lastNameKana"
            :first-name-kana="inquiry.firstNameKana"
            :birth-year-cd="inquiry.birthYearCd"
            :phone="inquiry.phone"
            :email="inquiry.email"
            :comment="inquiry.comment"
            
            @update:employment-type-cd="updateEmploymentTypeCd"
            @update:wishful-time-cd="updateWishfulTimeCd"
            @update:prefecture-cd="updatePrefectureCd"
            @update:city-cd="updateCityCd"
            @update:position-type-cds="updatePositionTypeCds"
            @update:last-name="updateLastName"
            @update:first-name="updateFirstName"
            @update:last-name-kana="updateLastNameKana"
            @update:first-name-kana="updateFirstNameKana"
            @update:birth-year-cd="updateBirthYearCd"
            @update:phone="updatePhone"
            @update:email="updateEmail"
            @update:comment="updateComment"
            @next="next"
            @back="back"
            @submit="post"
          />
        </KeepAlive>
      </Transition>
    </div>
  </div>
</template>